import { useState, memo, useRef, useEffect, useMemo } from "react";
import { DIGIT_SYMBOL, SymbolType } from "../models/constants";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { formatNumber, isEmpty } from "../utils";
import { QuoteStat } from "../models/proto/pricing_model_pb";

interface Props {
    symbol: string;
    symbolCd: string;
    symbolType: number;
}
const Symbol = (props: Props) => {
    const { symbol, symbolCd, symbolType } = props;
    const quote = useSelector((state: RootState) => state.quote[symbol]);
    const lastQuote = useSelector((state: RootState) => state.system.lastQuoteList[symbol]?.getQuoteStat());
    const preQuote = useRef({quote}).current;
    const [pipSize, setPipSize] = useState(0);
    const [spread, setSpread] = useState("");
    const [quoteStat, setQuoteStat] = useState<QuoteStat | undefined>(lastQuote);

    useMemo(() => {
        let digit = Number(DIGIT_SYMBOL.get(symbolCd)) - 1;
        if (symbolType === SymbolType.CFD) digit = digit + 1;
        setPipSize(Math.pow(10,digit));
    }, [symbolCd]);

    useEffect(() => {
        let spread = 0;
        spread = (+quote?.getAsk() - +quote?.getBid()) * pipSize;
        let spreadDigit = 1;
        if (symbolType === SymbolType.CFD) spreadDigit = 0;
        setSpread(formatNumber(spread, spreadDigit))
    }, [quote])

    const [detailSymbol, setDetailSymbol] = useState({
        bid: "",
        ask: "",
        spread: "",
        priceChange: 0,
        textColorBid: "",
        textColorAsk: "",
    });

    useEffect(() => {
        // console.log(quote)
        if (quote?.getQuoteStat()) setQuoteStat(quote?.getQuoteStat());
        if(!quote?.getBid()) return;
        setDetailSymbol({
            ...detailSymbol,
            bid: formatNumber(quote?.getBid(), DIGIT_SYMBOL.get(symbolCd) as number),
            ask: formatNumber(quote?.getAsk(), DIGIT_SYMBOL.get(symbolCd) as number),
            priceChange: priceChange(),
            textColorBid: textColor(+preQuote.quote?.getBid(), +quote?.getBid()),
            textColorAsk: textColor(+preQuote.quote?.getAsk(), +quote?.getAsk()),
        })
        preQuote.quote = quote;
    }, [quote,lastQuote]);

    const priceChange = () => {
        const priceClose = !!lastQuote?.getPriceClose ? +lastQuote.getPriceClose() : 0;
        const bid = +quote?.getBid();
        if (priceClose === 0 ) return 0;
        if(!bid && !priceClose) return 0;
        const percentChange = ((bid - priceClose)*100/priceClose);
        return +percentChange
    }

    const textColor = (prePrice: number, price: number) => {
        if(!price || !prePrice) return ""
        const priceChange = price - prePrice
        if(priceChange > 0) return "bg-danger text-white"
        if(priceChange < 0) return "bg-primary text-white"
        return ""
    }

    const convertCFDName = (sym: string) => {
        let name = "";
        switch (sym) {
            case "JPXJPY":
                name = "JPX/JPY（日本株指数）";
                break;
            case "DJIUSD":
                name = "DJI/USD（米株30）";
                break;
            case "XAUUSD":
                name = "XAU/USD（金）";
                break;
            case "XTIUSD":
                name = "XTI/USD（原油）";
                break;
        }
        return name;
    }

    return (
        <tr className="align-middle">

            <td className="pe-0 d-none d-md-table-cell">
                {symbolType === SymbolType.FX && <span className="text-ellipsis">
                    <img src={`images/${symbolCd.substring(0, 3)}.svg`} className="img-flag me-1" alt="" />
                    <img src={`images/${symbolCd.substring(3)}.svg`} className="img-flag" alt="" />
                </span>}

                {symbolType === SymbolType.CFD && <span className="text-ellipsis"><img src={`images/${symbolCd}.svg`} className="img-flag" alt="" /></span>}
            </td>
            <td className="ps-md-0">
                {symbolType === SymbolType.FX &&<span className="text-ellipsis d-block d-md-none mb-1">
                    <img src={`images/${symbolCd.substring(0, 3)}.svg`} className="img-flag me-1" alt="" />
                    <img src={`images/${symbolCd.substring(3)}.svg`} className="img-flag" alt="" />
                </span>}
                {symbolType === SymbolType.CFD && <span className="text-ellipsis d-block d-md-none mb-1">
                    <img src={`images/${symbolCd}.svg`} className="img-flag" alt="" />
                </span>}

                {symbolType === SymbolType.FX && <span className="text-ellipsis">{symbolCd.substring(0, 3) + "/" + symbolCd.substring(3)}</span>}
                {symbolType === SymbolType.CFD && <span className="text-ellipsis">{convertCFDName(symbolCd)}</span>}
            </td>
            <td className="text-end">
                <span className={`text-ellipsis px-1 d-block ${detailSymbol?.textColorBid}`}>{detailSymbol?.bid}</span>
                <span className={`text-ellipsis px-1 d-block d-md-none mt-1 ${detailSymbol?.textColorAsk}`}>{detailSymbol?.ask}</span>
            </td>
            <td className="text-center">
                {!isEmpty(detailSymbol?.bid) && <img src={`images/${detailSymbol?.textColorBid.includes('bg-danger') ? 'up' : 'down'}.svg`} className="img-arrow px-1 d-block mt-1" alt="" />}
                {!isEmpty(detailSymbol?.ask) && <img src={`images/${detailSymbol?.textColorAsk.includes('bg-danger') ? 'up' : 'down'}.svg`} className="img-arrow px-1 d-block d-md-none mt-1" alt="" />}
            </td>
            <td className="text-end d-none d-md-table-cell">
                <span className={`text-ellipsis px-1 d-block ${detailSymbol?.textColorAsk}`}>{detailSymbol?.ask}</span>
            </td>
            <td className="d-none d-md-table-cell">
                {!isEmpty(detailSymbol?.ask) && <img src={`images/${detailSymbol?.textColorAsk.includes('bg-danger') ? 'up' : 'down'}.svg`} className="img-arrow" alt="" />}
            </td>
            <td className="text-end">
                <span className="text-ellipsis">{spread}</span>
                {/* <span className={`text-ellipsis d-block d-md-none mt-1 ${detailSymbol.priceChange >= 0 ? 'text-danger' : 'text-primary'}`}>{formatNumber(detailSymbol.priceChange, 2)} %</span> */}
            </td>
            {/* <td className="text-end d-none d-md-table-cell">
                {!isEmpty(detailSymbol?.ask) && <span className={`text-ellipsis ${detailSymbol.priceChange >= 0 ? 'text-danger' : 'text-primary'}`}>{formatNumber(detailSymbol.priceChange, 2)} %</span>}
            </td>
            <td className="text-end">
                <span className="text-ellipsis">{formatNumber(quoteStat?.getBidHigh(), Number(DIGIT_SYMBOL.get(symbolCd)))}</span>
                <span className="text-ellipsis d-block d-md-none mt-1">{formatNumber(quoteStat?.getBidLow(), Number(DIGIT_SYMBOL.get(symbolCd)))}</span>
            </td>
            <td className="text-end d-none d-md-table-cell">
                <span className="text-ellipsis">{formatNumber(quoteStat?.getBidLow(), Number(DIGIT_SYMBOL.get(symbolCd)))}</span>
            </td> */}
        </tr>
    )
}
export default memo(Symbol);